import { Badge, Tabs } from "antd";
import { Fragment, useEffect, useState } from "react";
import { useQueryParam } from "use-query-params";

import { TAX_ADVISORY_PERMISSION_SCOPES, useAuth } from "@/modules/auth";
import type { EditableAccountAttributes } from "@/v1/types/IAccount.type";
import colors from "../../../../../themes/colors";
import {
  IUser,
  IUserDetails,
  KontaxUserStatus,
  KontaxUserStatusAdditionalParams,
} from "../../../../../types";
import { KontaxUserContext } from "../../../../contexts/KontaxUserContext";
import Anschreiben from "../../../Anschreiben/Anschreiben";
import DocumentsTree from "../../Common/UserDetails/DocumentsTree";
import { DocuwareDocuments } from "../../Common/UserDetails/DocumentsTree/DocuwareDocuments";
import { UserPages } from "../../Common/UserDetails/enums";
import BusinessAssetList from "./BusinessAssetList";
import { CustomerInformation } from "./CustomerInformation";
import DatevImport from "./DatevImport";
import DocumentsList from "./DocumentsList";
import { UserFilterOptions } from "../../Common/UserFilters";

const { TabPane } = Tabs;

const badgeStyle = {
  backgroundColor: colors.purple,
  color: colors.white,
};

const UserDetails = ({
  user,
  updateUserStatus,
  editUser,
  editAccount,
  updateDependents,
  deleteDependent,
  setEmailParam,
  setFilterOptions,
}: {
  user: IUser;
  editUser: (email: string, userDetails: IUserDetails) => Promise<void>;
  editAccount: (
    id: number,
    payload: EditableAccountAttributes
  ) => Promise<void>;
  updateUserStatus: (
    email: string,
    status: KontaxUserStatus | null,
    additionalParams?: KontaxUserStatusAdditionalParams
  ) => Promise<void>;
  updateDependents: Function;
  deleteDependent: (email: string, id: string) => Promise<void>;
  setEmailParam: (email: string) => void;
  setFilterOptions: (filterOptions: UserFilterOptions) => void;
}) => {
  const [userPageQueryParam, setUserPageQueryParam] = useQueryParam<
    UserPages | undefined
  >("userPage");
  const [businessAssetsCount, setBusinessAssetsCount] = useState<number>(0);
  const [documentsCount, setDocumentsCount] = useState<number>(0);
  const defaultActiveTab = userPageQueryParam || UserPages.INFORMATION;
  const [activeTab, setActiveTab] = useState<string>(defaultActiveTab);
  const { hasOperatorPermissionScope } = useAuth();
  const hasTaxAdvisoryPermissionScope = hasOperatorPermissionScope(
    TAX_ADVISORY_PERMISSION_SCOPES
  );

  useEffect(() => {
    if (userPageQueryParam) {
      // Clear `userPage` query param because it's intended to be used only once during initial render
      setUserPageQueryParam(undefined, "replaceIn");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <KontaxUserContext user={user}>
      <Tabs
        defaultActiveKey={defaultActiveTab}
        centered
        onChange={setActiveTab}
      >
        <TabPane
          tab={<span id="InformationTabButton">Information</span>}
          key={UserPages.INFORMATION}
        >
          <CustomerInformation
            user={user}
            editUser={editUser}
            editAccount={editAccount}
            updateUserStatus={updateUserStatus}
            updateDependents={updateDependents}
            deleteDependent={deleteDependent}
            setEmailParam={setEmailParam}
            setFilterOptions={setFilterOptions}
          />
        </TabPane>
        {hasTaxAdvisoryPermissionScope && (
          <TabPane
            forceRender={true}
            tab={
              <span id="DocumentsTabButton">
                Dokumente <Badge count={documentsCount} style={badgeStyle} />
              </span>
            }
            key={UserPages.DOCUMENTS}
          >
            <DocumentsTree
              user={user}
              editUser={editUser}
              categoryNames={["VERIFICATION", "OTHER"]}
              allowUpload
              setDocumentsCount={setDocumentsCount}
            />
            {activeTab === UserPages.DOCUMENTS && (
              <DocuwareDocuments accountId={user.accountId} />
            )}
          </TabPane>
        )}
        <TabPane
          forceRender={true}
          tab={
            <span id="AssetsTabButton">
              Anlagevermögen{" "}
              <Badge count={businessAssetsCount} style={badgeStyle} />
            </span>
          }
          key={UserPages.ASSETS}
        >
          <BusinessAssetList
            user={user}
            setBusinessAssetsCount={setBusinessAssetsCount}
          />
        </TabPane>
        {hasTaxAdvisoryPermissionScope && (
          <Fragment>
            <TabPane
              tab={<span id="ReceiptsTabButton">Belege</span>}
              key={UserPages.RECEIPTS}
            >
              <DocumentsList user={user} pageType={UserPages.RECEIPTS} />
            </TabPane>
            <TabPane
              tab={
                <span id="DateVImportTabButton">DTVF &amp; EXTF Import</span>
              }
              key={UserPages.DATEV_IMPORT}
            >
              <DatevImport user={user} />
            </TabPane>
            <TabPane
              tab={<span id="AnschreibenTabButton">Anschreiben</span>}
              key={UserPages.ANSCHREIBEN}
            >
              <Anschreiben user={user} />
            </TabPane>
          </Fragment>
        )}
      </Tabs>
    </KontaxUserContext>
  );
};

export default UserDetails;
